import { API_BASE_URL } from 'config/apiConfig';
import { auth } from 'services/api/authClient';
import { authRecoverySnapshotRequiresHandshake } from 'utils/authTransitions';
import { nativeInvoke, nativeListen, type NativeListener } from './nativeBridge';

const base64Url = (value: Uint8Array): string => btoa(String.fromCharCode(...Array.from(value)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

export async function createNativeOAuthProof(): Promise<{ verifier: string; challenge: string }> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return { verifier, challenge: base64Url(new Uint8Array(digest)) };
}

/** Construct the sole browser destination locally; API responses never choose a URL. */
export function nativeGoogleBrowserUrl(requestId: string, apiBase = API_BASE_URL): string {
  if (!/^[A-Za-z0-9_-]{43}$/.test(requestId)) throw new Error('Invalid Google sign-in request.');
  const base = new URL(apiBase);
  if (base.protocol !== 'https:' || base.username || base.password || base.search || base.hash || !['', '/'].includes(base.pathname)) {
    throw new Error('Google sign-in requires a secure WiseFriend API address.');
  }
  const url = new URL('/api/auth/google/initiate/', base.origin);
  url.searchParams.set('native_request', requestId);
  return url.toString();
}

const APP_SCHEMES: Record<string, string> = {
  'org.wisefriend.preview': 'wisefriend-poc',
  'org.wisefriend.staging': 'wisefriend-staging',
  'org.wisefriend.app': 'wisefriend',
};

export function nativeGoogleCompletion(raw: string, scheme: string, requestId: string): string | null {
  try {
    const url = new URL(raw);
    if (url.protocol !== `${scheme}:` || url.host !== 'open' || url.pathname !== '/oauth/google'
        || url.username || url.password || url.hash || Array.from(url.searchParams).length !== 2
        || url.searchParams.getAll('native_request').length !== 1 || url.searchParams.get('native_request') !== requestId
        || url.searchParams.getAll('completion').length !== 1) return null;
    const completion = url.searchParams.get('completion') || '';
    return /^[A-Za-z0-9_-]{43}$/.test(completion) ? completion : null;
  } catch { return null; }
}

const aborted = () => new DOMException('Sign-in canceled.', 'AbortError');
const assertActive = (signal: AbortSignal) => { if (signal.aborted) throw aborted(); };

export async function signInWithNativeGoogle(signal: AbortSignal, signupCode?: string) {
  await auth.waitForPendingLogout();
  assertActive(signal);
  const recoverySnapshot = auth.captureLogoutRecoverySnapshot();
  const { verifier, challenge } = await createNativeOAuthProof();
  const { id: appId } = await nativeInvoke<{ id: string }>('App', 'getInfo');
  const scheme = APP_SCHEMES[appId];
  if (!scheme) throw new Error('This WiseFriend application is not supported.');
  assertActive(signal);
  const start = await auth.startNativeGoogle(challenge, authRecoverySnapshotRequiresHandshake(recoverySnapshot), signal, appId, signupCode);
  const requestId = start.data.native_request;
  let completed = false;
  let listener: NativeListener | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let cancel: (() => void) | undefined;
  try {
    assertActive(signal);
    const browserUrl = nativeGoogleBrowserUrl(requestId);
    const expiresIn = start.data.expires_in;
    if (!Number.isFinite(expiresIn) || expiresIn <= 0 || expiresIn > 600) throw new Error('Invalid Google sign-in lifetime.');
    let receive: (completion: string) => void = () => undefined;
    const callback = new Promise<string>((resolve, reject) => {
      receive = resolve;
      cancel = () => reject(aborted());
      timer = setTimeout(() => reject(new Error('Google sign-in timed out. Please try again.')), expiresIn * 1000);
    });
    // Handle rejection even if cancellation arrives while Browser.open settles.
    void callback.catch(() => undefined);
    signal.addEventListener('abort', cancel!, { once: true });
    listener = await nativeListen<{ url: string }>('App', 'appUrlOpen', (event) => {
      const secret = nativeGoogleCompletion(event.url, scheme, requestId);
      if (secret) receive(secret);
    });
    assertActive(signal);
    await nativeInvoke('Browser', 'open', { url: browserUrl, presentationStyle: 'popover' });
    const completion = await callback;
    assertActive(signal);
    auth.assertLogoutRecoverySnapshotCurrent(recoverySnapshot);
    const response = await auth.exchangeNativeGoogle(requestId, verifier, completion, recoverySnapshot, signal);
    assertActive(signal);
    if (!response) throw new Error('Google sign-in is not complete. Please start again.');
    completed = true;
    return response;
  } finally {
    clearTimeout(timer);
    if (cancel) signal.removeEventListener('abort', cancel);
    void listener?.remove().catch(() => undefined);
    // Only a callback secret crosses the OS link. Verifier and application
    // credentials never leave the initiating app's API request context.
    void nativeInvoke('Browser', 'close').catch(() => undefined);
    if (!completed) void auth.cancelNativeGoogle(requestId, verifier).catch(() => undefined);
  }
}
